// Vendor Müşteri Mesajları — tüm firmalara gönderdiğimiz iletişimin tek panel'i
// + Broadcast (toplu) gönderim
import React, { useEffect, useState, useCallback } from 'react';
import api from '../services/api';
import { Button, Modal, FormField, EmptyState, ListPageHeader, FilterBar, Pager } from '../components/ui';

const fmtDate = (s) => s ? new Date(s).toLocaleString('tr-TR') : '—';

const CH_LABEL = { email: '📧 E-posta', sms: '📱 SMS', whatsapp: '💬 WhatsApp', in_app: '🔔 In-app' };
const STATUS_LABEL = {
  sent:    { txt: 'Gönderildi', clr: 'var(--success)', bg: 'rgba(16,185,129,0.12)' },
  pending: { txt: 'Bekliyor',   clr: '#d97706',        bg: 'rgba(245,158,11,0.12)' },
  failed:  { txt: 'Başarısız',  clr: 'var(--danger)',  bg: 'rgba(239,68,68,0.12)' },
};

export default function VendorMessagesPage() {
  const [list, setList] = useState([]);
  const [total, setTotal] = useState(0);
  const [summary, setSummary] = useState({});
  const [filters, setFilters] = useState({ channel: '', status: '', search: '', days: 90 });
  const [showFilters, setShowFilters] = useState(false);
  const [page, setPage] = useState(1);
  const [limit] = useState(50);
  const [loading, setLoading] = useState(false);
  const [composeOpen, setComposeOpen] = useState(false);
  const [detail, setDetail] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/vendor/messages', { params: { ...filters, page, limit } });
      if (r.data.success) {
        setList(r.data.data || []);
        setTotal(r.data.total || 0);
        setSummary(r.data.summary || {});
      }
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [filters, page, limit]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="stocks-page">
      <ListPageHeader
        title="Müşteri Mesajları"
        unit="mesaj"
        count={total}
        searchValue={filters.search}
        onSearch={(v) => setFilters(f => ({ ...f, search: v }))}
        searchPlaceholder="Konu, içerik, firma..."
        showFilterButton
        filtersActive={showFilters}
        onToggleFilters={() => setShowFilters(s => !s)}
        onRefresh={load}
        refreshing={loading}
        actions={<Button size="sm" onClick={() => setComposeOpen(true)}>✉️ Yeni Mesaj / Broadcast</Button>}
      />

      {/* Özet kartlar */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 180px), 1fr))',
                    gap: 10, padding: '0 12px 12px' }}>
        <SummaryCard label="Son 30g Gönderilen" value={summary.sent_30d || 0} clr="var(--success)" />
        <SummaryCard label="Son 30g Başarısız"   value={summary.failed_30d || 0} clr="var(--danger)" />
        <SummaryCard label="Bekleyen"            value={summary.pending || 0} clr="#d97706" />
        <SummaryCard label="Toplam (30g)"        value={summary.total || 0} clr="var(--accent-primary)" />
      </div>

      <FilterBar
        active={showFilters}
        onToggle={() => setShowFilters(s => !s)}
        values={filters}
        onChange={setFilters}
        fields={[
          { key: 'channel', type: 'select', label: 'Kanal', options: [
            { value: '', label: 'Tümü' }, { value: 'email', label: '📧 E-posta' },
            { value: 'sms', label: '📱 SMS' }, { value: 'whatsapp', label: '💬 WhatsApp' },
            { value: 'in_app', label: '🔔 In-app' },
          ]},
          { key: 'status', type: 'select', label: 'Durum', options: [
            { value: '', label: 'Tümü' }, { value: 'sent', label: 'Gönderildi' },
            { value: 'pending', label: 'Bekliyor' }, { value: 'failed', label: 'Başarısız' },
          ]},
          { key: 'days', type: 'select', label: 'Dönem', options: [
            { value: 7, label: 'Son 7 gün' }, { value: 30, label: 'Son 30 gün' },
            { value: 90, label: 'Son 90 gün' }, { value: 365, label: 'Son 1 yıl' },
          ]},
        ]}
      />

      <div className="stocks-table-wrap">
        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-tertiary)' }}>Yükleniyor...</div>
        ) : list.length === 0 ? (
          <EmptyState title="Bu kriterlerde mesaj yok" subtitle="Yeni Mesaj butonuyla toplu duyuru gönderebilirsin" />
        ) : (
          <table className="stocks-table">
            <thead>
              <tr>
                <th style={{ width: 130 }}>Tarih</th>
                <th style={{ width: 80 }}>Kanal</th>
                <th>Firma</th>
                <th>Konu</th>
                <th style={{ width: 120 }}>Gönderen</th>
                <th style={{ width: 120 }}>Durum</th>
              </tr>
            </thead>
            <tbody>
              {list.map(m => {
                const st = STATUS_LABEL[m.status] || STATUS_LABEL.pending;
                return (
                  <tr key={m.id} className="stocks-table-row" onClick={() => setDetail(m)}>
                    <td><span style={{ fontSize: '0.78rem', color: 'var(--text-secondary)' }}>{fmtDate(m.created_at)}</span></td>
                    <td>{CH_LABEL[m.channel] || m.channel}</td>
                    <td>
                      <strong>{m.company_name || '—'}</strong>
                      {m.company_code && <span style={{ fontSize: '0.72rem', color: 'var(--text-tertiary)', marginLeft: 6 }}>{m.company_code}</span>}
                      <div style={{ fontSize: '0.72rem', color: 'var(--text-tertiary)' }}>{m.to_address}</div>
                    </td>
                    <td style={{ maxWidth: 320 }}>
                      <div style={{ fontWeight: 600, color: 'var(--text-primary)', fontSize: '0.85rem',
                                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {m.subject || <em style={{ color: 'var(--text-tertiary)' }}>(konu yok)</em>}
                      </div>
                      <div style={{ fontSize: '0.72rem', color: 'var(--text-tertiary)',
                                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {(m.body || '').replace(/<[^>]+>/g, '').slice(0, 80)}
                      </div>
                    </td>
                    <td><span style={{ fontSize: '0.78rem' }}>{m.sent_by_name || '—'}</span></td>
                    <td>
                      <span style={{ fontSize: '0.7rem', padding: '3px 9px', borderRadius: 100, fontWeight: 700,
                                     background: st.bg, color: st.clr }}>{st.txt}</span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <div style={{ padding: '8px 12px' }}>
        <Pager page={page} total={total} limit={limit} onPage={setPage} />
      </div>

      {composeOpen && <ComposeModal onClose={() => setComposeOpen(false)} onSent={() => { setComposeOpen(false); load(); }} />}
      {detail && <DetailModal data={detail} onClose={() => setDetail(null)} />}
    </div>
  );
}

function SummaryCard({ label, value, clr }) {
  return (
    <div style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-secondary)', borderRadius: 8,
                  padding: '12px 14px', borderTop: `3px solid ${clr || 'var(--border-secondary)'}` }}>
      <div style={{ fontSize: '0.68rem', color: 'var(--text-tertiary)', textTransform: 'uppercase',
                    letterSpacing: '0.04em', marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: '1.4rem', fontWeight: 700, color: clr || 'var(--text-primary)',
                    fontVariantNumeric: 'tabular-nums' }}>{value}</div>
    </div>
  );
}

/* ── Compose / Broadcast Modal ───────────────────────────────────── */
function ComposeModal({ onClose, onSent }) {
  const [targetType, setTargetType] = useState('all');
  const [targetStatus, setTargetStatus] = useState('active');
  const [targetPlanId, setTargetPlanId] = useState('');
  const [targetCompanyIds, setTargetCompanyIds] = useState([]);
  const [channel, setChannel] = useState('email');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [plans, setPlans] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [companySearch, setCompanySearch] = useState('');
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    api.get('/vendor/plans').then(r => { if (r.data.success) setPlans(r.data.data || []); });
    api.get('/vendor/companies', { params: { limit: 500 } }).then(r => { if (r.data.success) setCompanies(r.data.data || []); });
  }, []);

  const submit = async () => {
    if (!body.trim()) return alert('İçerik boş olamaz');
    if (targetType === 'specific' && targetCompanyIds.length === 0) return alert('En az 1 firma seç');

    const confirmCount = targetType === 'all' ? companies.length
                       : targetType === 'specific' ? targetCompanyIds.length
                       : '?';
    if (!window.confirm(`${confirmCount} firmaya mesaj gönderilecek. Emin misin?`)) return;

    setSending(true); setResult(null);
    try {
      const r = await api.post('/vendor/messages/broadcast', {
        target_type: targetType,
        target_status: targetType === 'by_status' ? targetStatus : null,
        target_plan_id: targetType === 'by_plan' ? targetPlanId : null,
        target_company_ids: targetType === 'specific' ? targetCompanyIds : [],
        channel, subject, body,
      });
      if (r.data.success) setResult(r.data.data);
    } catch (err) {
      setResult({ error: err.response?.data?.message || 'Hata' });
    }
    setSending(false);
  };

  const filteredCompanies = companies.filter(c => {
    if (!companySearch) return true;
    const s = companySearch.toLowerCase();
    return (c.name || '').toLowerCase().includes(s) || (c.code || '').toLowerCase().includes(s);
  });

  return (
    <Modal title="✉️ Yeni Mesaj / Broadcast" onClose={onClose} size="lg">
      <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 14 }}>
        {result ? (
          <div style={{ padding: 16, borderRadius: 8,
                        background: result.error ? 'rgba(239,68,68,0.08)' : 'rgba(16,185,129,0.08)',
                        border: '1px solid ' + (result.error ? 'rgba(239,68,68,0.25)' : 'rgba(16,185,129,0.25)') }}>
            {result.error ? (
              <div style={{ color: 'var(--danger)' }}>✕ {result.error}</div>
            ) : (
              <>
                <div style={{ fontWeight: 700, color: 'var(--success)', fontSize: '1rem', marginBottom: 6 }}>
                  ✓ Gönderim tamamlandı
                </div>
                <div style={{ color: 'var(--text-secondary)', fontSize: '0.88rem' }}>
                  Toplam: <b>{result.total}</b> · Gönderilen: <b style={{ color: 'var(--success)' }}>{result.sent}</b> ·
                  Başarısız: <b style={{ color: 'var(--danger)' }}>{result.failed}</b>
                </div>
                <div style={{ marginTop: 12 }}>
                  <Button onClick={onSent}>Kapat</Button>
                </div>
              </>
            )}
          </div>
        ) : (
          <>
            <FormField label="Hedef Kitle" required>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 8 }}>
                {[
                  { v: 'all',       l: '🌍 Tüm firmalar' },
                  { v: 'by_status', l: '📊 Duruma göre' },
                  { v: 'by_plan',   l: '💎 Plana göre' },
                  { v: 'specific',  l: '🎯 Seçili firmalar' },
                ].map(o => (
                  <label key={o.v} style={{
                    padding: '10px 12px', borderRadius: 8, cursor: 'pointer',
                    background: targetType === o.v ? 'rgba(124,58,237,0.12)' : 'var(--bg-secondary)',
                    border: '1px solid ' + (targetType === o.v ? 'rgba(124,58,237,0.40)' : 'var(--border-secondary)'),
                    fontSize: '0.85rem', fontWeight: targetType === o.v ? 600 : 500,
                    color: targetType === o.v ? '#7c3aed' : 'var(--text-primary)',
                    display: 'flex', alignItems: 'center', gap: 6,
                  }}>
                    <input type="radio" name="tgt" checked={targetType === o.v} onChange={() => setTargetType(o.v)} style={{ display: 'none' }} />
                    {o.l}
                  </label>
                ))}
              </div>
            </FormField>

            {targetType === 'by_status' && (
              <FormField label="Durum">
                <select value={targetStatus} onChange={e => setTargetStatus(e.target.value)}>
                  <option value="active">Aktif</option>
                  <option value="trial">Trial</option>
                  <option value="trial_expired">Trial Bitti</option>
                  <option value="past_due">Vade Geçti</option>
                  <option value="suspended">Askıda</option>
                </select>
              </FormField>
            )}

            {targetType === 'by_plan' && (
              <FormField label="Plan">
                <select value={targetPlanId} onChange={e => setTargetPlanId(e.target.value)}>
                  <option value="">— Seç —</option>
                  {plans.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </FormField>
            )}

            {targetType === 'specific' && (
              <FormField label={`Firmalar (${targetCompanyIds.length} seçili)`}>
                <input className="ui-input" value={companySearch} onChange={e => setCompanySearch(e.target.value)} placeholder="Ara..." />
                <div style={{ maxHeight: 200, overflowY: 'auto', border: '1px solid var(--border-secondary)',
                              borderRadius: 8, marginTop: 6, background: 'var(--bg-secondary)' }}>
                  {filteredCompanies.map(c => (
                    <label key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 8,
                                                padding: '7px 10px', cursor: 'pointer', borderBottom: '1px solid var(--border-secondary)' }}>
                      <input type="checkbox" checked={targetCompanyIds.includes(c.id)}
                             onChange={e => setTargetCompanyIds(prev => e.target.checked ? [...prev, c.id] : prev.filter(x => x !== c.id))} />
                      <span style={{ fontSize: '0.85rem', flex: 1 }}><b>{c.code}</b> · {c.name}</span>
                      <span style={{ fontSize: '0.7rem', color: 'var(--text-tertiary)' }}>{c.subscription_status}</span>
                    </label>
                  ))}
                </div>
              </FormField>
            )}

            <FormField label="Kanal">
              <select value={channel} onChange={e => setChannel(e.target.value)}>
                <option value="email">📧 E-posta</option>
                <option value="sms" disabled>📱 SMS (yakında)</option>
                <option value="whatsapp" disabled>💬 WhatsApp (yakında)</option>
              </select>
            </FormField>

            <FormField label="Konu" hint="Şablon değişkeni: {{firma_adi}}, {{kullanici_adi}}, {{firma_kodu}}">
              <input className="ui-input" value={subject} onChange={e => setSubject(e.target.value)}
                     placeholder="Örn: Merhaba {{firma_adi}}, yeni özelliklerimizi keşfedin" />
            </FormField>

            <FormField label="İçerik" required hint="HTML destekler. {{firma_adi}} gibi değişkenleri otomatik dolduruyoruz.">
              <textarea className="ui-input" value={body} onChange={e => setBody(e.target.value)}
                        rows={10} placeholder="<p>Sayın {{firma_adi}},</p><p>...</p>"
                        style={{ fontFamily: 'monospace', fontSize: '0.82rem' }} />
            </FormField>

            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', paddingTop: 8,
                          borderTop: '1px solid var(--border-secondary)' }}>
              <Button variant="ghost" onClick={onClose}>İptal</Button>
              <Button onClick={submit} disabled={sending || !body.trim()}>{sending ? 'Gönderiliyor...' : '🚀 Gönder'}</Button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}

/* ── Detail Modal ────────────────────────────────────────────────── */
function DetailModal({ data, onClose }) {
  const st = STATUS_LABEL[data.status] || STATUS_LABEL.pending;
  return (
    <Modal title="Mesaj Detayı" onClose={onClose} size="lg">
      <div style={{ padding: 16 }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12, marginBottom: 14 }}>
          <Field label="Firma" value={data.company_name + (data.company_code ? ` (${data.company_code})` : '')} />
          <Field label="Alıcı" value={data.to_address} mono />
          <Field label="Kanal" value={CH_LABEL[data.channel] || data.channel} />
          <Field label="Gönderen" value={data.sent_by_name || '—'} />
          <Field label="Oluşturuldu" value={fmtDate(data.created_at)} />
          <Field label="Gönderildi" value={data.sent_at ? fmtDate(data.sent_at) : '—'} />
          <Field label="Durum" value={
            <span style={{ fontSize: '0.7rem', padding: '3px 9px', borderRadius: 100, fontWeight: 700,
                           background: st.bg, color: st.clr }}>{st.txt}</span>
          } />
          {data.template_code && <Field label="Şablon" value={data.template_code} mono />}
        </div>
        {data.subject && (
          <div style={{ marginBottom: 12 }}>
            <div style={{ fontSize: '0.7rem', color: 'var(--text-tertiary)', textTransform: 'uppercase',
                          letterSpacing: '0.04em', marginBottom: 4 }}>Konu</div>
            <div style={{ fontSize: '0.95rem', fontWeight: 600, color: 'var(--text-primary)' }}>{data.subject}</div>
          </div>
        )}
        <div>
          <div style={{ fontSize: '0.7rem', color: 'var(--text-tertiary)', textTransform: 'uppercase',
                        letterSpacing: '0.04em', marginBottom: 6 }}>İçerik</div>
          <div style={{ padding: 12, background: 'var(--bg-secondary)', border: '1px solid var(--border-secondary)',
                        borderRadius: 8, fontSize: '0.85rem', lineHeight: 1.6, maxHeight: 360, overflowY: 'auto' }}
               dangerouslySetInnerHTML={{ __html: data.body }} />
        </div>
        {data.error_message && (
          <div style={{ marginTop: 12, padding: 10, borderRadius: 8,
                        background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.25)',
                        color: 'var(--danger)', fontSize: '0.85rem' }}>
            <strong>Hata:</strong> {data.error_message}
          </div>
        )}
      </div>
    </Modal>
  );
}

function Field({ label, value, mono }) {
  return (
    <div>
      <div style={{ fontSize: '0.68rem', color: 'var(--text-tertiary)', textTransform: 'uppercase',
                    letterSpacing: '0.04em', marginBottom: 3 }}>{label}</div>
      <div style={{ fontSize: '0.86rem', color: 'var(--text-primary)', fontFamily: mono ? 'monospace' : 'inherit' }}>{value}</div>
    </div>
  );
}
